import type { GameResponse } from '@warcarp/api-types'
import { mergeWireEvents, type WireEventState } from './mergeWireEvents'

/** The version fields carried by a full game state or a state patch. */
export interface VersionedWireState extends WireEventState {
  /** Monotonic Game.stateVersion assigned by persistGameState. */
  stateVersion?: number
}

function versionOf(state: VersionedWireState | null | undefined): number | undefined {
  return typeof state?.stateVersion === 'number' ? state.stateVersion : undefined
}

function eventCountOf(state: WireEventState | null | undefined): number | undefined {
  return typeof state?.eventCount === 'number' ? state.eventCount : undefined
}

/**
 * Returns true when `incoming` cannot be newer than the cached state.
 *
 * stateVersion is the authoritative ordering key. eventCount is the fallback
 * for older servers/fixtures and also protects the append-only event pointer
 * when a frame has no stateVersion. Equal versions are accepted because the
 * same frame may legitimately be delivered more than once (for example after
 * a reconnect); the merge is idempotent.
 */
export function isStaleGameFrame(
  current: VersionedWireState | null | undefined,
  incoming: VersionedWireState | null | undefined,
): boolean {
  const currentVersion = versionOf(current)
  const incomingVersion = versionOf(incoming)
  if (
    currentVersion !== undefined &&
    incomingVersion !== undefined &&
    incomingVersion < currentVersion
  ) {
    return true
  }

  const currentEventCount = eventCountOf(current)
  const incomingEventCount = eventCountOf(incoming)
  return (
    currentEventCount !== undefined &&
    incomingEventCount !== undefined &&
    incomingEventCount < currentEventCount
  )
}

/**
 * Reconcile a full HTTP/WS game frame with the cache. Stale frames are
 * discarded before they can roll back phase, eliminations, territories, or
 * lastConquest. Newer/equal frames retain the accumulated event tail.
 */
export function reconcileGameFrame(
  current: GameResponse | undefined,
  incoming: GameResponse,
): GameResponse {
  if (!current || !current.state || !incoming.state) return incoming

  const currentState = current.state as VersionedWireState
  const incomingState = incoming.state as VersionedWireState
  if (isStaleGameFrame(currentState, incomingState)) return current

  const merged = mergeWireEvents(currentState, incomingState)
  return {
    ...incoming,
    state: {
      ...incoming.state,
      events: merged.events,
      eventCount: merged.eventCount,
    },
  }
}

/** Apply a bot-loop state patch without allowing an older patch to roll back. */
export function reconcileGameStatePatch(
  current: GameResponse,
  incomingState: Record<string, unknown>,
): GameResponse {
  const incoming = incomingState as VersionedWireState
  if (isStaleGameFrame(current.state as VersionedWireState | null, incoming)) return current

  const merged = mergeWireEvents(current.state as VersionedWireState, incoming)
  return {
    ...current,
    state: {
      ...incomingState,
      events: merged.events,
      eventCount: merged.eventCount,
    },
  }
}
